import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  TrendingUp,
  AlertTriangle,
  PercentCircle,
  Plus,
  Pencil,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { EmptyState } from "@/components/sartn/EmptyState";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { useLanguageStore } from "@/stores/languageStore";
import type {
  SubscriptionPlan,
  Subscriber,
  SubscriberStatus,
  PlanDuration,
} from "@/stores/subscriptionStore";
import { cn } from "@/lib/cn";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };
const STAGGER_STATS = 0.04;
const STAGGER_ITEMS = 0.06;

function formatETB(amount: number): string {
  return `${amount.toLocaleString("en-US")} ETB`;
}

/* ---------- Stat cards ---------- */

interface MiniStatProps {
  icon: LucideIcon;
  label: string;
  value: string;
  accentColor: string;
  index: number;
}

function MiniStat({ icon: Icon, label, value, accentColor, index }: MiniStatProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * STAGGER_STATS, duration: 0.25 }}
      className="flex flex-col gap-1.5 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-3 shadow-sm"
    >
      <div
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]",
          accentColor,
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <span className="font-headline text-lg font-bold text-[var(--color-text)]">{value}</span>
      <span className="text-xs font-medium text-[var(--color-text-secondary)]">
        {label}
      </span>
    </motion.div>
  );
}

/* ---------- Duration badge ---------- */

const DURATION_COLORS: Record<PlanDuration, string> = {
  weekly: "bg-cyan-50 text-cyan-700",
  monthly: "bg-blue-50 text-blue-700",
  quarterly: "bg-violet-50 text-violet-700",
  yearly: "bg-amber-50 text-amber-700",
};

const DURATION_LABELS: Record<PlanDuration, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

function DurationBadge({ duration }: { duration: PlanDuration }) {
  return (
    <span
      className={cn(
        "rounded-[var(--radius-full)] px-2 py-0.5 text-[10px] font-medium",
        DURATION_COLORS[duration],
      )}
    >
      {DURATION_LABELS[duration]}
    </span>
  );
}

/* ---------- Status badge ---------- */

const STATUS_COLORS: Record<SubscriberStatus, string> = {
  active: "bg-green-50 text-green-700",
  grace: "bg-amber-50 text-amber-700",
  expired: "bg-red-50 text-red-700",
  cancelled: "bg-gray-50 text-gray-700",
};

const STATUS_LABELS: Record<SubscriberStatus, string> = {
  active: "Active",
  grace: "Grace",
  expired: "Expired",
  cancelled: "Cancelled",
};

function StatusBadge({ status }: { status: SubscriberStatus }) {
  return (
    <span
      className={cn(
        "rounded-[var(--radius-full)] px-2 py-0.5 text-xs font-medium",
        STATUS_COLORS[status],
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/* ---------- Toggle switch ---------- */

function ToggleSwitch({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        "relative h-6 w-10 shrink-0 rounded-full transition-colors",
        checked ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]",
      )}
      role="switch"
      aria-checked={checked}
      aria-label={checked ? "Deactivate plan" : "Activate plan"}
    >
      <span
        className={cn(
          "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform",
          checked && "translate-x-4",
        )}
      />
    </button>
  );
}

/* ---------- Plan card ---------- */

function PlanCard({
  plan,
  index,
  onToggle,
}: {
  plan: SubscriptionPlan;
  index: number;
  onToggle: () => void;
}) {
  const language = useLanguageStore((state) => state.language);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * STAGGER_ITEMS, duration: 0.25 }}
      className={cn(
        "flex items-center gap-3 rounded-[var(--radius-lg)] shadow-sm",
        "bg-[var(--color-surface)] p-3",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-[var(--color-text)]">
            {plan.name}
          </span>
          <DurationBadge duration={plan.duration} />
        </div>
        <span className="text-xs text-[var(--color-text-secondary)]">
          {formatETB(plan.price)}
        </span>
        <div className="flex items-center gap-1 text-xs text-[var(--color-text-muted)]">
          <Users className="h-3 w-3" />
          <span>{plan.subscriberCount} subscribers</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <ToggleSwitch checked={plan.isActive} onToggle={onToggle} />
        <button
          type="button"
          disabled
          className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]
                     text-[var(--color-text-muted)] opacity-50"
          aria-label={`Edit ${plan.name} pending`}
          title={language === "am" ? "የማስተካከያ API ገና ነው" : "Edit API pending"}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

/* ---------- Time progress bar ---------- */

function TimeProgressBar({
  daysLeft,
  totalDays,
}: {
  daysLeft: number;
  totalDays: number;
}) {
  const percent = totalDays > 0 ? Math.min((daysLeft / totalDays) * 100, 100) : 0;

  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-border)]">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 0.5, ease: "easeOut" as const }}
        className={cn(
          "h-full rounded-full",
          percent > 30 ? "bg-[var(--color-primary)]" : "bg-amber-500",
          percent === 0 && "bg-red-500",
        )}
      />
    </div>
  );
}

/* ---------- Subscriber card ---------- */

const DURATION_TOTAL_DAYS: Record<PlanDuration, number> = {
  weekly: 7,
  monthly: 30,
  quarterly: 90,
  yearly: 365,
};

function guessTotalDays(planName: string, plans: SubscriptionPlan[]): number {
  const match = plans.find((p) => p.name === planName);
  return match ? DURATION_TOTAL_DAYS[match.duration] : 30;
}

function SubscriberCard({
  subscriber,
  plans,
  index,
}: {
  subscriber: Subscriber;
  plans: SubscriptionPlan[];
  index: number;
}) {
  const totalDays = guessTotalDays(subscriber.planName, plans);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * STAGGER_ITEMS, duration: 0.25 }}
      className={cn(
        "flex flex-col gap-2 rounded-[var(--radius-lg)] shadow-sm",
        "bg-[var(--color-surface)] p-3",
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold text-[var(--color-text)]">
            {subscriber.name}
          </span>
          <span className="text-xs text-[var(--color-text-muted)]">
            {subscriber.planName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={subscriber.status} />
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)]">
        <span>
          {subscriber.daysLeft > 0
            ? `${subscriber.daysLeft} days left`
            : "Expired"}
        </span>
        <span>
          {subscriber.endDate}
        </span>
      </div>

      <TimeProgressBar daysLeft={subscriber.daysLeft} totalDays={totalDays} />
    </motion.div>
  );
}

/* ---------- Main page ---------- */

export default function SubscriptionsPage() {
  const { plans, subscribers, stats, isLoading, fetchPlans, fetchSubscribers, togglePlan } =
    useSubscriptionStore();

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    const firstChannelId = plans[0]?.channelId;
    if (firstChannelId) void fetchSubscribers(firstChannelId);
  }, [fetchSubscribers, plans]);

  function navigateToNewPlan() {
    window.location.hash = "/subscriptions/new-plan";
  }

  if (isLoading && plans.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Subscriptions" onBack={() => window.history.back()} />
        <LoadingSkeleton variant="card" count={4} className="px-4 pt-4" />
      </div>
    );
  }

  if (!isLoading && plans.length === 0 && subscribers.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Subscriptions" onBack={() => window.history.back()} />
        <EmptyState
          icon={Users}
          title="No subscriptions yet"
          description="Create your first plan to start managing paid channel subscriptions."
          actionLabel="Create Plan"
          onAction={navigateToNewPlan}
        />
      </div>
    );
  }

  const statCards = [
    { icon: Users, label: "Active", value: String(stats.activeSubscribers), accentColor: "bg-green-50 text-green-600" },
    { icon: TrendingUp, label: "Revenue", value: formatETB(stats.totalRevenue), accentColor: "bg-blue-50 text-blue-600" },
    { icon: AlertTriangle, label: "Expiring", value: String(stats.expiringThisWeek), accentColor: "bg-amber-50 text-amber-600" },
    { icon: PercentCircle, label: "Churn", value: `${stats.churnRate}%`, accentColor: "bg-red-50 text-red-600" },
  ];

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title="Subscriptions" onBack={() => window.history.back()} />

      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="flex-1 px-4 pb-24 pt-2"
      >
        <FeatureStatusBanner
          status="connected"
          title="Live subscriptions / ቀጥታ ሰብስክሪፕሽን"
          description="Plans and subscriber records are loaded through the authenticated subscription API. / ፕላኖችና የተመዝጋቢ መዝገቦች በተረጋገጠ API ይጫናሉ።"
        />

        <div className="mb-6 grid grid-cols-2 gap-3">
          {statCards.map((stat, index) => (
            <MiniStat key={stat.label} {...stat} index={index} />
          ))}
        </div>

        <section className="mb-6">
          <h2 className="mb-3 text-base font-semibold text-[var(--color-text)]">
            Plans
          </h2>
          <div className="flex flex-col gap-2">
            {plans.map((plan, index) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                index={index}
                onToggle={() => togglePlan(plan.id)}
              />
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-base font-semibold text-[var(--color-text)]">
            Subscribers
          </h2>
          {subscribers.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              No subscribers yet. They will appear here when users subscribe.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {subscribers.map((subscriber, index) => (
                <SubscriberCard
                  key={subscriber.id}
                  subscriber={subscriber}
                  plans={plans}
                  index={index}
                />
              ))}
            </div>
          )}
        </section>
      </motion.main>

      <motion.button
        type="button"
        onClick={navigateToNewPlan}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={SPRING}
        className="fixed bottom-20 right-4 z-20 flex h-14 w-14 items-center justify-center
                   rounded-full bg-[var(--color-primary)] text-white shadow-lg
                   active:bg-[var(--color-primary-hover)]"
        aria-label="Create new plan"
      >
        <Plus className="h-6 w-6" />
      </motion.button>
    </div>
  );
}
